// angular imports
import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';

// CC-Common imports
import { Utils } from 'cc-common';

// devextreme imports
import { DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// component imports
import { BoardApprovalBaseComponent } from '../../../board-approval/_base/board-approval-base.component';

// constant imports
import { BoardApprovalProcessConstants } from '../../_constants/board-approval-process-constants';
import { BoardApprovalActionConstants } from '../../_constants/board-approvals-action-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';

// model imports
import { BoardApprovalItemResultDto } from '../../_models/board-approval-item-result-dto';
import { BoardApprovalActionsResultDto } from '../../_models/board-approval-actions-result-dto';
import { BoardApprovalActionParameterDto } from '../../_models/board-approval-action-parameter-dto';
import { BoardApprovalEditorParameterDto } from '../../_models/board-approval-editor-parameter-dto';
import { BoardApprovalCollectionReferralResultDto } from '../../_models/board-approval-collection-referral-result-dto';
import { BoardApprovalDetailParameterDto } from '../../_models/board-approval-detail-parameter-dto';
import { BoardApprovalWFParameterDto } from '../../_models/board-approval-wf-parameter-dto';

@Component({
    selector: 'app-board-approval-editor',
    templateUrl: './board-approval-editor.component.html',
    styleUrls: ['./board-approval-editor.component.scss']
})
export class BoardApprovalEditorComponent extends BoardApprovalBaseComponent implements OnInit, OnChanges {
    @ViewChild('boardApprovalEditor', { static: false }) boardApprovalEditor: DxPopupComponent;
    @ViewChild('boardApprovalForm', { static: false }) boardApprovalForm: DxFormComponent;

    @Input() visible = false;
    @Input() isClosed: boolean;
    @Input() selectedRequest: BoardApprovalItemResultDto;
    @Input() mode: string;

    @Output() onClose = new EventEmitter<boolean>();

    processEditorResult: any;
    actionParameter: BoardApprovalActionParameterDto;
    boardApprovalNoteParameter: BoardApprovalActionParameterDto;

    isApprovalStatusChanged = false;

    showBoardApprovalNote = false;
    boardApprovalNoteTitle: string;

    wfCheckImage: any;
    wfCheckImageBlob: any;

    showCollectionReferral = false;
    collectionsDetail: BoardApprovalCollectionReferralResultDto;

    showAccDetail = false;
    showAccEditor = false;
    accEditorParam: any = {};
    accEditorTitle: string;
    showWorkflowViewer = false;
    processButtonTitle: string;
    appliedAction: BoardApprovalActionsResultDto;

    constructor(protected readonly injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.getButtonTitle();
    }

    ngOnChanges() {
        this.populateRequest();
    }

    onPopupShown(): void {
        this.boardApprovalEditor.instance.repaint();
    }

    onPopupHidden(): void {
        this.closeEditor();
    }

    onCancelClick(): void {
        this.closeEditor();
    }

    onActionClick(action: BoardApprovalActionsResultDto): void {
        if (
            Utils.notNullAndDefined(this.boardApprovalForm) &&
            Utils.notNullAndDefined(this.boardApprovalForm.instance)
        ) {
            const validator = this.boardApprovalForm.instance.validate();
            if (Utils.isTrue(validator.isValid)) {
                if (
                    Utils.isNullOrUndefined(this.selectedRequest.userInfo) ||
                    Utils.notEqualsIgnoreCase(this.selectedRequest.userInfo.id, this.actionParameter.actionOnBehalfOf)
                ) {
                    const dialog = this.helperService.getConfirmationDialog(
                        'Confirmation',
                        `This action will set the status to ${action.displayTextForWarningMessage} on <br />behalf of all approvers. Are you sure you want to continue?`
                    );
                    dialog.show().then((dialogResult) => {
                        if (Utils.isTrue(dialogResult)) {
                            this.applyAction(action);
                        }
                    });
                } else {
                    this.applyAction(action);
                }
            }
        }
    }

    onDetailsClick(): void {
        switch (this.selectedRequest.process) {
            case BoardApprovalProcessConstants.ACC:
                this.accEditorParam.communityId = this.selectedRequest.communityId;
                this.accEditorParam.accItemId = Number(this.selectedRequest.processId);
                this.accEditorParam.customerId = this.processEditorResult.customerId;
                if (Utils.isTrue(this.processEditorResult.allowSave)) {
                    this.accEditorTitle = `Edit Architectural Application. Project ID: ${this.selectedRequest.processId}`;
                    this.showAccEditor = true;
                } else {
                    this.accEditorTitle = `Architectural Application. Project ID: ${this.selectedRequest.processId}`;
                    this.showAccDetail = true;
                }
                break;
            case BoardApprovalProcessConstants.COLLECTIONS:
                this.populateCollectionDetail();
                break;
            case BoardApprovalProcessConstants.WF:
                this.showWorkflowViewer = true;
                break;
        }
    }

    closeBoardApprovalNotes(data: any): void {
        this.showBoardApprovalNote = false;
        if (Utils.notNullAndDefined(data)) {
            this.denyOrApproveWithStipulation(data);
        }
    }

    downloadWfCheckImage(): void {
        const fileName = `Invoice_${this.selectedRequest.processId}_checkimage.jpg`;
        this.helperService.showBlobInBrowserTab(this.wfCheckImageBlob, fileName);
    }

    private getButtonTitle(): void {
        let title: string = null;
        switch (this.selectedRequest.process) {
            case BoardApprovalProcessConstants.ACC:
                title = 'ACC';
                break;
            case BoardApprovalProcessConstants.COLLECTIONS:
                title = 'AR Collections';
                break;
            case BoardApprovalProcessConstants.WF:
                title = 'AP Workflow';
                break;
        }
        this.processButtonTitle = `View ${title} Details`;
    }

    private populateCollectionDetail(): void {
        const param: BoardApprovalDetailParameterDto = new BoardApprovalDetailParameterDto();
        param.communityId = this.selectedRequest.communityId;
        param.processId = this.selectedRequest.processId;
        this.boardApprovalHelperService.showLoading();
        this.sharedBoardApprovalService.getCollectionsDetail(param).subscribe(
            (result: BoardApprovalCollectionReferralResultDto) => {
                this.collectionsDetail = result;
                this.showCollectionReferral = true;
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.boardApprovalHelperService.hideLoading();
            }
        );
    }

    private applyAction(action: BoardApprovalActionsResultDto): void {
        this.appliedAction = action;
        if (Utils.notNullAndDefined(this.selectedRequest)) {
            this.actionParameter.boardMemberUserId =
                Utils.notNullAndDefined(this.selectedRequest.userInfo) &&
                Utils.notNullAndDefined(this.selectedRequest.userInfo.id)
                    ? this.selectedRequest.userInfo.id
                    : null;
            this.actionParameter.addedOn = this.selectedRequest.addedOn;
        } else {
            this.actionParameter.boardMemberUserId = null;
        }
        this.actionParameter.clearCache = true;
        if (Utils.isTrue(action.reasonRequiredForAction) || Utils.isNullOrUndefined(action.reasonRequiredForAction)) {
            this.boardApprovalNoteTitle = action.displayTextForWarningMessage;
            this.boardApprovalNoteParameter = cloneDeep(this.actionParameter);
            this.boardApprovalNoteParameter.notes = null;
            this.showBoardApprovalNote = true;
        } else {
            this.approveOrReview(action.displayTextForWarningMessage);
        }
    }

    private denyOrApproveWithStipulation(data: BoardApprovalActionParameterDto): void {
        let service: Observable<any>;
        if (Utils.notNullAndDefined(this.selectedRequest) && Utils.notNullAndDefined(this.selectedRequest.process)) {
            if (Utils.equalsIgnoreCase(this.boardApprovalNoteTitle, 'Approved with Stipulation')) {
                switch (this.selectedRequest.process) {
                    case BoardApprovalProcessConstants.ACC:
                        service = this.sharedBoardApprovalService.approveWithStipulationBoardApproval(data);
                        break;
                    case BoardApprovalProcessConstants.COLLECTIONS:
                        service = this.sharedBoardApprovalService.approveWithStipulationBoardApprovalCollections(data);
                        break;
                    case BoardApprovalProcessConstants.WF:
                        service = this.sharedBoardApprovalService.approveWithStipulationBoardApprovalWf(data);
                        break;
                }
            } else {
                switch (this.selectedRequest.process) {
                    case BoardApprovalProcessConstants.ACC:
                        service = this.sharedBoardApprovalService.denyBoardApproval(data);
                        break;
                    case BoardApprovalProcessConstants.COLLECTIONS:
                        service = this.sharedBoardApprovalService.denyBoardApprovalCollections(data);
                        break;
                    case BoardApprovalProcessConstants.WF:
                        service = this.sharedBoardApprovalService.denyBoardApprovalWf(data);
                        break;
                }
            }
        }

        if (Utils.notNullAndDefined(service)) {
            this.boardApprovalHelperService.showLoading();
            service.subscribe(
                () => {
                    notify(
                        `${this.boardApprovalNoteTitle} Successfully`,
                        'Success',
                        MessageInterval.successMsgInterval
                    );
                    this.isApprovalStatusChanged = true;
                    this.boardApprovalHelperService.hideLoading();

                    // Adding delay of 500ms for loader to be removed from screen due to above code line
                    setTimeout(() => {
                        this.closeEditor();
                    }, 500);
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.boardApprovalHelperService.hideLoading();
                }
            );
        }
    }

    private approveOrReview(message: string): void {
        this.actionParameter.responseToOwner = null;
        this.actionParameter.notes = null;
        let service: Observable<any>;
        if (Utils.notNullAndDefined(this.selectedRequest) && Utils.notNullAndDefined(this.selectedRequest.process)) {
            if (Utils.equalsIgnoreCase(message, 'Approved')) {
                switch (this.selectedRequest.process) {
                    case BoardApprovalProcessConstants.ACC:
                        service = this.sharedBoardApprovalService.approveBoardApproval(this.actionParameter);
                        break;
                    case BoardApprovalProcessConstants.COLLECTIONS:
                        service = this.sharedBoardApprovalService.approveBoardApprovalCollections(this.actionParameter);
                        break;
                    case BoardApprovalProcessConstants.WF:
                        service = this.sharedBoardApprovalService.approveBoardApprovalWf(this.actionParameter);
                        break;
                }
            } else {
                switch (this.selectedRequest.process) {
                    case BoardApprovalProcessConstants.ACC:
                        service = this.sharedBoardApprovalService.holdBoardApproval(this.actionParameter);
                        break;
                    case BoardApprovalProcessConstants.COLLECTIONS:
                        service = this.sharedBoardApprovalService.holdBoardApprovalCollections(this.actionParameter);
                        break;
                    case BoardApprovalProcessConstants.WF:
                        service = this.sharedBoardApprovalService.holdBoardApprovalWf(this.actionParameter);
                        break;
                }
            }
        }
        if (Utils.notNullAndDefined(service)) {
            this.boardApprovalHelperService.showLoading();
            service.subscribe(
                () => {
                    notify(`${message} Successfully`, 'Success', MessageInterval.successMsgInterval);
                    this.isApprovalStatusChanged = true;
                    this.boardApprovalHelperService.hideLoading();

                    // Adding delay of 500ms for loader to be removed from screen due to above code line
                    setTimeout(() => {
                        this.closeEditor();
                    }, 500);
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.boardApprovalHelperService.hideLoading();
                }
            );
        }
    }

    private populateRequest(clearCache = false): void {
        if (Utils.notNullAndDefined(this.selectedRequest)) {
            this.populateRequestEditor(clearCache);
        }
    }

    private populateRequestEditor(clearCache: boolean): void {
        const baseParams: BoardApprovalEditorParameterDto = new BoardApprovalEditorParameterDto();
        baseParams.isClosed = this.isClosed;
        baseParams.communityId = this.selectedRequest.communityId;
        baseParams.processId = this.selectedRequest.processId;
        baseParams.addedOn = this.selectedRequest.addedOn;
        baseParams.clearCache = clearCache;

        let service: Observable<any>;
        if (Utils.notNullAndDefined(this.selectedRequest) && Utils.notNullAndDefined(this.selectedRequest.process)) {
            baseParams.boardMemberUserId =
                Utils.notNullAndDefined(this.selectedRequest.userInfo) &&
                Utils.notNullAndDefined(this.selectedRequest.userInfo.id)
                    ? this.selectedRequest.userInfo.id
                    : null;

            switch (this.selectedRequest.process) {
                case BoardApprovalProcessConstants.ACC:
                    service = this.sharedBoardApprovalService.getBoardApprovalACCEditor(baseParams);
                    break;
                case BoardApprovalProcessConstants.COLLECTIONS:
                    service = this.sharedBoardApprovalService.getBoardApprovalCollectionsEditor(baseParams);
                    break;
                case BoardApprovalProcessConstants.WF:
                    service = this.sharedBoardApprovalService.getBoardApprovalWorkflowEditor(baseParams);
                    break;
            }

            if (Utils.notNullAndDefined(service)) {
                this.boardApprovalHelperService.showLoading();
                service.subscribe(
                    (result: any) => {
                        this.processEditorResult = result;
                        if (Utils.equalsIgnoreCase(this.selectedRequest.process, BoardApprovalProcessConstants.WF)) {
                            this.populateWfCheckImage();
                        }
                        this.prepareDefaults();
                        this.boardApprovalHelperService.hideLoading();
                    },
                    (error) => {
                        this.errorService.displayError(error);
                        this.boardApprovalHelperService.hideLoading();
                    }
                );
            }
        }
    }

    private prepareDefaults(): void {
        this.actionParameter = new BoardApprovalActionParameterDto();
        this.actionParameter.communityId = this.selectedRequest.communityId;
        this.actionParameter.isClosed = this.isClosed;
        this.actionParameter.processId = this.selectedRequest.processId;
        this.actionParameter.notes = this.processEditorResult.notes;
        this.actionParameter.responseToOwner = Utils.notNullAndDefined(
            this.processEditorResult.existingResponseToCustomer
        )
            ? this.processEditorResult.existingResponseToCustomer
            : null;

        const defaultOnBehalfOf = this.processEditorResult.onBehalfOf.find((d) => d.isDefault);
        this.actionParameter.actionOnBehalfOf = Utils.notNullAndDefined(defaultOnBehalfOf)
            ? defaultOnBehalfOf.key
            : null;

        this.processEditorResult.actions.forEach((action: BoardApprovalActionsResultDto) => {
            switch (action.imageIndicator) {
                case BoardApprovalActionConstants.GREEN_CHECK:
                    action.type = 'success';
                    action.icon = this.iconCheck;
                    break;
                case BoardApprovalActionConstants.RED_CROSS:
                    action.type = 'danger';
                    action.icon = this.iconCross;
                    break;
                case BoardApprovalActionConstants.WARNING:
                    action.type = 'default';
                    action.icon = this.iconWarn;
                    break;
                default:
                    action.type = 'default';
                    action.icon = null;
                    break;
            }
        });
    }

    private populateWfCheckImage(): void {
        const baseParams: BoardApprovalWFParameterDto = new BoardApprovalWFParameterDto();
        baseParams.communityId = this.selectedRequest.communityId;
        baseParams.workFlowId = Number(this.selectedRequest.processId);

        this.boardApprovalHelperService.showLoading();
        this.sharedBoardApprovalService.getWfCheckImage(baseParams).subscribe(
            (result: Blob) => {
                this.wfCheckImageBlob = result;
                this.wfCheckImage = this.helperService.getImageSafeUrlFromBlob(result);
                this.boardApprovalHelperService.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.boardApprovalHelperService.hideLoading();
            }
        );
    }

    private closeEditor(): void {
        this.visible = false;
        this.onClose.emit(this.isApprovalStatusChanged);
    }
}
